import { AddIcon, CheckIcon, ContentCopyIcon, DeleteIcon, ExpandMoreIcon, InfoOutlinedIcon, SmartToyIcon } from '../theme/icons';
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField,
  Alert, Chip, IconButton, Tooltip, Divider, CircularProgress,
  Table, TableBody, TableCell, TableHead, TableRow, Collapse,
} from '@mui/material';
import { DS } from '../theme/designSystem';
import {
  listApiKeys, createApiKey, revokeApiKey, getAgentSnapshots,
  TenantApiKey, AgentSnapshot, CreatedApiKey,
} from '../services/api';

const AgentSetupCard: React.FC = () => {
  const [keys, setKeys]               = useState<TenantApiKey[]>([]);
  const [snapshots, setSnapshots]     = useState<AgentSnapshot[]>([]);
  const [newKeyName, setNewKeyName]   = useState('');
  const [creating, setCreating]       = useState(false);
  const [loading, setLoading]         = useState(true);
  const [newKey, setNewKey]           = useState<CreatedApiKey | null>(null);
  const [copied, setCopied]           = useState(false);
  const [copiedCmd, setCopiedCmd]     = useState(false);
  const [error, setError]             = useState('');
  const [revoking, setRevoking]       = useState<number | null>(null);
  const [howOpen, setHowOpen]         = useState(false);

  // Include the app's base path ("/lens" in the combined image) so agents call
  // e.g. https://cloudforge.cloud/lens/api/... — bare /api/* on the shared ALB
  // routes to a different service.
  const serverUrl = `${window.location.origin}${process.env.PUBLIC_URL || ''}`;

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [k, s] = await Promise.all([listApiKeys(), getAgentSnapshots()]);
      setKeys(k);
      setSnapshots(s);
    } catch {
      // silently ignore auth errors — parent already handles 401
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const handleCreate = async () => {
    if (!newKeyName.trim()) return;
    setCreating(true); setError('');
    try {
      const key = await createApiKey(newKeyName.trim());
      setNewKey(key);
      setNewKeyName('');
      await reload();
    } catch (e: any) {
      setError(e.response?.data?.error ?? 'Failed to create API key.');
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (id: number) => {
    if (!window.confirm('Revoke this API key? The agent using it will stop sending data.')) return;
    setRevoking(id);
    try {
      await revokeApiKey(id);
      if (newKey?.id === id) setNewKey(null);
      await reload();
    } finally {
      setRevoking(null);
    }
  };

  const copy = async (text: string, which: 'key' | 'cmd') => {
    await navigator.clipboard.writeText(text);
    if (which === 'key') { setCopied(true); setTimeout(() => setCopied(false), 2000); }
    else { setCopiedCmd(true); setTimeout(() => setCopiedCmd(false), 2000); }
  };

  const dockerCmd = (key: string) =>
    `docker run -d --name cloudlens-agent --restart unless-stopped \\\n` +
    `  -e CLOUDLENS_ENDPOINT="${serverUrl}" \\\n` +
    `  -e CLOUDLENS_API_KEY="${key}" \\\n` +
    `  -e AZURE_TENANT_ID="YOUR_TENANT_ID" \\\n` +
    `  -e AZURE_CLIENT_ID="YOUR_CLIENT_ID" \\\n` +
    `  -e AZURE_CLIENT_SECRET="YOUR_CLIENT_SECRET" \\\n` +
    `  -e AZURE_SUBSCRIPTION_IDS="SUB_ID_1,SUB_ID_2" \\\n` +
    `  -e COLLECTION_INTERVAL_MINUTES="30" \\\n` +
    `  cloudforge/cloudlens-agent:latest`;

  // Exact data set collected by the agent — keep in sync with
  // agent/CloudLens.Agent/Collectors/AzureDataCollector.cs
  const collectedData = [
    ['Resource inventory', 'Every resource with name, type, location, resource group, SKU, provisioning state and tags.'],
    ['Cost summary', 'Month-to-date actual spend from the Cost Management API, grouped by service and resource group.'],
    ['Network exposure', 'Risky NSG rules (inbound Allow from Internet / any source) and public IP addresses with DNS names.'],
    ['Access control', 'RBAC role assignments at subscription scope, with privileged roles (Owner, Contributor, …) flagged.'],
    ['Activity log', 'Control-plane operations from the last 30 days (operation, caller, status).'],
  ];

  const latestSnapshot = snapshots.sort(
    (a, b) => new Date(b.receivedAt).getTime() - new Date(a.receivedAt).getTime()
  )[0];

  return (
    <Card sx={{ borderRadius: '14px', border: DS.border, boxShadow: DS.shadow, mb: 3 }}>
      <CardContent sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
          <Box sx={{
            width: 36, height: 36, borderRadius: '10px',
            background: DS.grad, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <SmartToyIcon sx={{ color: 'white', fontSize: 20 }} />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={700}>Agent Setup</Typography>
            <Typography variant="caption" color="text.secondary">
              Deploy the CloudLens Agent in your Azure subscription to collect data automatically
            </Typography>
          </Box>
        </Box>

        {/* How the agent works (collapsible) */}
        <Box sx={{ border: DS.border, borderRadius: '10px', mb: 3, overflow: 'hidden' }}>
          <Box
            onClick={() => setHowOpen((o) => !o)}
            sx={{
              display: 'flex', alignItems: 'center', gap: 1,
              p: '10px 14px', cursor: 'pointer', userSelect: 'none',
              background: DS.gradSubtle,
              '&:hover': { background: DS.gradCell },
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 18, color: DS.accent }} />
            <Typography variant="subtitle2" fontWeight={700} sx={{ flex: 1 }}>
              How the agent works
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
              Self-hosted · outbound-only · read-only
            </Typography>
            <ExpandMoreIcon sx={{
              fontSize: 20, color: 'text.secondary',
              transform: howOpen ? 'rotate(180deg)' : 'none',
              transition: 'transform 0.2s',
            }} />
          </Box>

          <Collapse in={howOpen}>
            <Divider />
            <Box sx={{ p: 2 }}>
              {/* Data flow */}
              <Typography variant="body2" sx={{ mb: 1.5 }}>
                The CloudLens Agent is a small container you run <strong>inside your own environment</strong>.
                It signs in to Azure with <strong>your</strong> service principal, collects read-only snapshots
                on a schedule (every 30 minutes by default), and pushes them outbound over HTTPS to{' '}
                <Box component="code" sx={{ fontSize: 12, fontFamily: 'monospace' }}>
                  {serverUrl}/api/agent/ingest
                </Box>
                , authenticated with the API key you generate below. <strong>No inbound connectivity is
                required</strong>, and your Azure credentials never leave your network — only the collected
                snapshot data is sent.
              </Typography>

              <Box component="ol" sx={{ m: 0, mb: 2, pl: 2.5, '& li': { mb: 0.5, fontSize: '0.875rem' } }}>
                <li>Agent container starts in your environment (Docker, ACI, Container Apps, …).</li>
                <li>It reads your Azure subscriptions using your service principal (read-only roles).</li>
                <li>Every collection cycle it pushes a snapshot to this CloudLens host over HTTPS (443).</li>
                <li>CloudLens stores the snapshot against your account, per subscription — nothing calls back in.</li>
              </Box>

              {/* Data collected */}
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                What data is collected
              </Typography>
              <Box component="ul" sx={{ m: 0, mb: 2, pl: 2.5, '& li': { mb: 0.5, fontSize: '0.875rem' } }}>
                {collectedData.map(([title, desc]) => (
                  <li key={title}>
                    <strong>{title}</strong> — {desc}
                  </li>
                ))}
              </Box>

              {/* Permissions required */}
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                Permissions required
              </Typography>
              <Table size="small" sx={{ mb: 2 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Requirement</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Scope</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Used for</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell><strong>Reader</strong> role (service principal)</TableCell>
                    <TableCell>Subscription</TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>
                      Resource inventory, NSG / public IP, RBAC and activity-log data
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>Cost Management Reader</strong> role</TableCell>
                    <TableCell>Subscription</TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>
                      Cost summaries (optional — cost data is skipped without it)
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>Outbound HTTPS (443)</strong></TableCell>
                    <TableCell>To this CloudLens host only</TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>
                      Snapshot upload — no inbound ports or firewall openings needed
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>

              {/* Security note */}
              <Alert severity="info" sx={{ borderRadius: '10px' }}>
                <Typography variant="caption" component="div">
                  <strong>Security:</strong> the API key is revocable at any time from this card and is bound
                  to the account that created it; snapshots are stored per account and subscription. All
                  transfer happens over TLS, and the agent never modifies your Azure resources — it only reads.
                </Typography>
              </Alert>
            </Box>
          </Collapse>
        </Box>

        {/* Last sync status */}
        {latestSnapshot && (
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 2,
            p: 2, mb: 3, borderRadius: '10px',
            background: 'rgba(218,111,73,0.06)',
            border: '1px solid rgba(218,111,73,0.2)',
          }}>
            <Chip label="Connected" color="success" size="small" />
            <Box>
              <Typography variant="body2" fontWeight={600}>
                {latestSnapshot.subscriptionName ?? latestSnapshot.subscriptionId}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Last sync: {new Date(latestSnapshot.receivedAt).toLocaleString()} ·{' '}
                {latestSnapshot.resourceCount} resources · agent v{latestSnapshot.agentVersion}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Step 1: Create API Key */}
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
          Step 1 — Generate an API Key
        </Typography>

        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <TextField
            size="small"
            placeholder='e.g. "Production Agent"'
            value={newKeyName}
            onChange={(e) => setNewKeyName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
            sx={{ flex: 1 }}
          />
          <Button
            variant="contained"
            startIcon={creating ? <CircularProgress size={14} color="inherit" /> : <AddIcon />}
            onClick={handleCreate}
            disabled={creating || !newKeyName.trim()}
            sx={{ background: DS.grad, borderRadius: '8px', textTransform: 'none', whiteSpace: 'nowrap' }}
          >
            {creating ? 'Creating…' : 'Create Key'}
          </Button>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }}>{error}</Alert>}

        {/* Newly created key — show once */}
        {newKey && (
          <Alert
            severity="success"
            sx={{ mb: 2, borderRadius: '10px' }}
            action={
              <Tooltip title={copied ? 'Copied!' : 'Copy key'}>
                <IconButton size="small" onClick={() => copy(newKey.key, 'key')}>
                  {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            }
          >
            <Typography variant="caption" fontWeight={700} display="block" mb={0.5}>
              Copy your API key now — it won't be shown again.
            </Typography>
            <Box sx={{
              fontFamily: 'monospace', fontSize: 12,
              background: 'rgba(0,0,0,0.06)', borderRadius: '6px', p: '4px 8px',
              wordBreak: 'break-all',
            }}>
              {newKey.key}
            </Box>
          </Alert>
        )}

        {/* Key list */}
        {loading ? (
          <CircularProgress size={20} sx={{ display: 'block', mx: 'auto', my: 2 }} />
        ) : keys.length > 0 ? (
          <Table size="small" sx={{ mb: 3 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Key</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Last used</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {keys.map((k) => (
                <TableRow key={k.id}>
                  <TableCell>{k.name}</TableCell>
                  <TableCell>
                    <Box sx={{ fontFamily: 'monospace', fontSize: 12, color: 'text.secondary' }}>
                      {k.keyPrefix}••••••••
                    </Box>
                  </TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontSize: 12 }}>
                    {k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleString() : 'Never'}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Revoke">
                      <IconButton
                        size="small"
                        color="error"
                        disabled={revoking === k.id}
                        onClick={() => handleRevoke(k.id)}
                      >
                        {revoking === k.id
                          ? <CircularProgress size={14} />
                          : <DeleteIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            No active API keys. Create one above to get started.
          </Typography>
        )}

        <Divider sx={{ my: 2.5 }} />

        {/* Step 2: Docker run */}
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
          Step 2 — Run the Agent
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Run this command on any machine with Docker (or deploy to Azure Container Instances / Container Apps):
        </Typography>

        <Box sx={{ position: 'relative' }}>
          <Box sx={{
            fontFamily: 'monospace', fontSize: 12,
            background: '#0f172a', color: '#e2e8f0',
            borderRadius: '10px', p: 2,
            whiteSpace: 'pre',
            overflowX: 'auto',
            lineHeight: 1.7,
          }}>
            {dockerCmd(newKey?.key ?? '<YOUR_API_KEY>')}
          </Box>
          <Tooltip title={copiedCmd ? 'Copied!' : 'Copy command'}>
            <IconButton
              size="small"
              onClick={() => copy(dockerCmd(newKey?.key ?? '<YOUR_API_KEY>'), 'cmd')}
              sx={{
                position: 'absolute', top: 8, right: 8,
                color: copiedCmd ? 'var(--ds-accent-2)' : '#94a3b8',
              }}
            >
              {copiedCmd ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Image <Box component="code" sx={{ fontFamily: 'monospace' }}>cloudforge/cloudlens-agent:latest</Box>{' '}
          (image publishing in progress — contact us for the current registry path).
        </Typography>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Managed Identity (recommended for Azure deployments): assign Reader + Cost Management Reader roles
          to the container's identity and remove the SP env vars above.
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Prefer not to run it yourself? We can host the agent for you — contact support.
        </Typography>

        {/* Snapshot table */}
        {snapshots.length > 0 && (
          <>
            <Divider sx={{ my: 2.5 }} />
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
              Connected Subscriptions
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Subscription</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Resources</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Last Sync</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Agent</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {snapshots.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {s.subscriptionName ?? s.subscriptionId}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {s.subscriptionId}
                      </Typography>
                    </TableCell>
                    <TableCell>{s.resourceCount.toLocaleString()}</TableCell>
                    <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>
                      {new Date(s.receivedAt).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <Chip label={`v${s.agentVersion}`} size="small" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default AgentSetupCard;
